import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Clock, CheckCircle, XCircle, AlertCircle, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const articleTypeLabels = {
  research: 'Recherche empirique',
  case_study: 'Étude de cas',
  review: 'Revue de littérature',
  practice_note: 'Note de pratique',
  protocol: 'Protocole'
};

export default function ReviewPage() {
  const { user, getAuthHeaders } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({ decision: '', comments: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPendingArticles();
  }, []);

  const fetchPendingArticles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/articles/pending-review`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        setArticles(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch articles:', e);
    } finally {
      setLoading(false);
    }
  };

  const openReviewDialog = (article) => {
    setSelectedArticle(article);
    setReviewForm({ decision: '', comments: '' });
    setReviewDialogOpen(true);
  };

  const handleReview = async () => {
    if (!reviewForm.decision || !reviewForm.comments.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/articles/${selectedArticle.article_id}/review`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(reviewForm)
      });

      if (res.ok) {
        toast.success(
          reviewForm.decision === 'approve' ? 'Article approuvé et publié' :
          reviewForm.decision === 'reject' ? 'Article refusé' :
          'Révisions demandées'
        );
        setReviewDialogOpen(false);
        fetchPendingArticles();
      }
    } catch (e) {
      toast.error('Erreur lors de l\'évaluation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="review-page" className="min-h-screen py-12 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold">Évaluations</h1>
          <p className="text-muted-foreground mt-1">
            Articles en attente d'évaluation
          </p>
        </div>

        {/* Stats */}
        <Card className="mb-8">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{articles.length}</p>
                <p className="text-sm text-muted-foreground">Articles en attente</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Articles List */}
        {articles.length > 0 ? (
          <div className="space-y-4">
            {articles.map((article) => (
              <Card key={article.article_id} data-testid={`review-card-${article.article_id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {articleTypeLabels[article.article_type] || article.article_type}
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                          <Clock className="w-3 h-3 mr-1" />
                          En attente
                        </Badge>
                      </div>
                      <CardTitle className="font-serif text-xl">{article.title}</CardTitle>
                      <CardDescription className="mt-2">
                        Par {article.author_name} • Soumis le {new Date(article.updated_at).toLocaleDateString('fr-FR')}
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => openReviewDialog(article)}
                      data-testid={`review-btn-${article.article_id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Évaluer
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm font-medium mb-2">Résumé</p>
                    <p className="text-sm text-muted-foreground">{article.abstract}</p>
                  </div>
                  {article.keywords && article.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-4">
                      {article.keywords.map((keyword, idx) => (
                        <span key={idx} className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h3 className="font-serif font-semibold text-lg mb-2">Tout est à jour !</h3>
                <p className="text-muted-foreground">
                  Aucun article en attente d'évaluation pour le moment.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Review Dialog */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Évaluer l'article</DialogTitle>
            </DialogHeader>
            
            {selectedArticle && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-serif font-semibold text-lg mb-2">{selectedArticle.title}</h3>
                  <p className="text-sm text-muted-foreground">Par {selectedArticle.author_name}</p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">Résumé</p>
                  <p className="text-sm text-muted-foreground">{selectedArticle.abstract}</p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <p className="text-sm font-medium mb-2">Contenu</p>
                  <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {selectedArticle.content}
                  </div>
                </div>

                {selectedArticle.references && selectedArticle.references.length > 0 && (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm font-medium mb-2">Références</p>
                    <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                      {selectedArticle.references.map((ref, idx) => (
                        <li key={idx}>{ref}</li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>Décision</Label>
                    <Select 
                      value={reviewForm.decision} 
                      onValueChange={(value) => setReviewForm({ ...reviewForm, decision: value })}
                    >
                      <SelectTrigger data-testid="decision-select">
                        <SelectValue placeholder="Choisir une décision" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approve">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            Approuver et publier
                          </div>
                        </SelectItem>
                        <SelectItem value="revision_needed">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-orange-600" />
                            Révisions nécessaires
                          </div>
                        </SelectItem>
                        <SelectItem value="reject">
                          <div className="flex items-center gap-2">
                            <XCircle className="w-4 h-4 text-red-600" />
                            Refuser
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Commentaires pour l'auteur</Label>
                    <Textarea
                      placeholder="Vos commentaires et recommandations..."
                      value={reviewForm.comments}
                      onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })}
                      rows={4}
                      data-testid="review-comments-textarea"
                    />
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleReview} disabled={submitting} data-testid="submit-review-btn">
                {submitting ? 'Envoi...' : 'Soumettre l\'évaluation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
